import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLoader } from '../../../hooks/useLoader';
import { useConfirm } from '../../../hooks/useConfirm';
import { Modal, Form, Button, Label, Icon } from 'semantic-ui-react';
import { DatePicker } from '../../../components/shared/DatePicker';
import PropTypes from 'prop-types';
import { Rates } from '../../../api/services/next';
import { onError, onSuccess, onWarning } from '../../../components/shared/messages';
import { useMessageResources, useTextResources } from '../../../language';
import moment from 'moment';
import { isBrowser, isMobileOnly } from 'mobile-device-detect';
import { ERateCategory, dateFormat } from '../../../components/shared/constants';
import { SyncRateToChannelManager } from '../../../api/services/pms';
import { toServerDateFormat } from '../../../components/shared/utils';

export const ExcludedDatesPopup = ({
    open,
    onClose,
    onSaveComplete,
    currentPromotion
}) => {
    const text = useTextResources();
    const message = useMessageResources();
    const loader = useLoader();
    const confirm = useConfirm();
    const [startDate, setStartDate] = useState(moment().startOf("day").toDate());
    const [endDate, setEndDate] = useState(moment().startOf("day").add(1, "day").toDate());

    const [isSaving, setIsSaving] = useState(false);
    const [selectedDates, setSelectedDates] = useState(currentPromotion?.RateExcludedDates ?? []);
    const [filterFromDate, setFilterFromDate] = useState(null);
    const [filterToDate, setFilterToDate] = useState(null);
    const [appliedFilterFromDate, setAppliedFilterFromDate] = useState(null);
    const [appliedFilterToDate, setAppliedFilterToDate] = useState(null);

    useEffect(() => {
        if (moment(endDate).isBefore(startDate)) {
            setEndDate(moment(startDate).add(1, "day").toDate());
        }
    }, [startDate, endDate]);

    useEffect(() => {
        if (currentPromotion) {
            setSelectedDates(currentPromotion?.RateExcludedDates ?? []);
        }
    }, [currentPromotion]);

    useEffect(() => {
        if (open && currentPromotion) {
            const today = moment().startOf('day').toDate();
            const promotionEndDate = currentPromotion.EndDate ? 
                moment(currentPromotion.EndDate).toDate() : null;
            
            setFilterFromDate(today);
            setFilterToDate(promotionEndDate);
            setAppliedFilterFromDate(today);
            setAppliedFilterToDate(promotionEndDate);
        }
    }, [open, currentPromotion]);

    const filteredExcludedDates = useMemo(() => {
        if (!selectedDates?.length) return [];
        
        return selectedDates.filter(dateRange => {
            if (!appliedFilterFromDate && !appliedFilterToDate) {
                return true;
            }
            
            const rangeStart = moment(dateRange.StartDate);
            const rangeEnd = moment(dateRange.EndDate);
            const filterStart = appliedFilterFromDate ? 
                moment(appliedFilterFromDate).startOf('day') : null;
            const filterEnd = appliedFilterToDate ? 
                moment(appliedFilterToDate).endOf('day') : null;
            
            if (filterStart && rangeEnd.isBefore(filterStart)) {
                return false;
            }
            if (filterEnd && rangeStart.isAfter(filterEnd)) {
                return false;
            }
            
            return true;
        }).sort((a, b) => {
            const aEnd = moment(a.EndDate);
            const bEnd = moment(b.EndDate);
            return aEnd.isSameOrBefore(bEnd) ? -1 : 1;
        });
    }, [selectedDates, appliedFilterFromDate, appliedFilterToDate]);

    const formatRange = (start, end) => {
        return `${toServerDateFormat(start)}_${toServerDateFormat(end)}`;
    }
    const dateIndexMap = useMemo(() => {
        const map = new Map();
        selectedDates.forEach((d, index) => {
            const key = formatRange(d.StartDate, d.EndDate);
            map.set(key, index);
        });
        return map;
    }, [selectedDates]);

    const validationError = useMemo(() => {
        if (filterFromDate && filterToDate && 
            moment(filterFromDate).isAfter(moment(filterToDate))) {
            return text("StartDateMustBeBeforeEndDate");
        }
        return null;
    }, [filterFromDate, filterToDate, text]);

    const addDateToList = () => {
        const exceedsBoundaries = moment(startDate).isBefore(currentPromotion.StartDate, 'day') ||
            moment(endDate).isAfter(currentPromotion.EndDate, 'day');

        if (exceedsBoundaries) {
            onWarning(message('WarningExcludedDatesRange'));
            return;
        }

        const isInsideExistingRange = selectedDates.some((d) =>
            moment(startDate).isSameOrAfter(d.StartDate, 'day') &&
            moment(endDate).isSameOrBefore(d.EndDate, 'day')
        );

        const includesExistingRange = selectedDates.some((d) =>
            moment(startDate).isSameOrBefore(d.StartDate, 'day') &&
            moment(endDate).isSameOrAfter(d.EndDate, 'day')
        );

        if (isInsideExistingRange || includesExistingRange) {
            onWarning(text('WarningDuplicateDates'));
            return;
        }

        setSelectedDates([
            ...selectedDates,
            {
                Id: 0,
                IsActive: true,
                DiscountId: currentPromotion.Id,
                StartDate: startDate,
                EndDate: endDate,
                ExcludedDays: calculateDateDifference(startDate, endDate),
                RateId: null
            }
        ])
    }
    const calculateDateDifference = (startDateTime, endDateTime) => {
        return moment(endDateTime).diff(moment(startDateTime), 'days');
    }

    const onRemoveDateFromSelectedDates = async (index, e) => {
        if (e) {
            const selectedDate = selectedDates[index];
            const dateRange = `${moment(selectedDate.StartDate).format(dateFormat)} - ${moment(selectedDate.EndDate).format(dateFormat)}`;
            
            const confirmed = await confirm({
                content: text('DeleteExcludedDates') + ' ' + dateRange + '?',
                confirmButton: {
                    content: text('Confirm'),
                    positive: true,
                },
                cancelButton: {
                    content: text('Cancel'),
                    negative: true,
                },
            });

            if (confirmed) {
                setSelectedDates(selectedDates => selectedDates.filter((_, i) => i !== index));
            }
        }
    };

    const saveAction = async (close) => {
        try {
            setIsSaving(true);
            loader(true);
            const requestData = {
                excludedDates: selectedDates,
                discountId: currentPromotion.Id
            };
            await Rates.SaveRateExcludedDates(requestData);
            
            await SyncRateToChannelManager({
                PMSHotelID: currentPromotion.Rates[0].HotelId,
                ExternalPMSRateID: currentPromotion.Id,
                eRateCategory: currentPromotion.Rates?.length ? ERateCategory.MultiRateDiscount : ERateCategory.FullRate
            });
                
            onSuccess();
            await onSaveComplete(selectedDates);
            return true;

        } catch (error) {
            onError(error);
        }
        finally {
            loader(false);
            setIsSaving(false);

            if (close) {
                closeAction();
            }
        }
    }

    const closeAction = () => {
        setFilterFromDate(null);
        setFilterToDate(null);
        setAppliedFilterFromDate(null);
        setAppliedFilterToDate(null);
        
        if (onClose) onClose();
    };

    const applyFilters = () => {
        setAppliedFilterFromDate(filterFromDate);
        setAppliedFilterToDate(filterToDate);
    }

    // Auto-apply filters when filter dates change
    useEffect(() => {
        applyFilters();
    }, [filterFromDate, filterToDate]);

    const clearFilters = useCallback(() => {
        console.log('clidcked clear filters');
        setFilterFromDate(null);
        setFilterToDate(null);
    }, []);

    const buttonSize = { style: { minWidth: '110px' } };
    return (
        <Modal size={isMobileOnly ? "fullscreen" : "small"} className={isMobileOnly ? "mobile" : ""} open={open} style={{ zIndex: 1000000 }} >
            <Modal.Header>{text("ExcludedDateRanges")} - {currentPromotion?.Name}</Modal.Header>
            <Modal.Content style={isMobileOnly ? { overflowY: "auto" } : { minHeight: 500, maxHeight: 500, overflowY: "auto" }}>
                <Form size="small" className={!currentPromotion || currentPromotion.IsActive ? '' : 'disabled'}>
                    
                    <Form.Group>
                        <Form.Field width={8}>
                            <DatePicker
                                label={`${text("FilterBy")} ${text("FromDate")}`}
                                value={filterFromDate}
                                onChange={setFilterFromDate}
                                placeholder={text("SelectStartDate")}
                                clearable={false}
                            />
                        </Form.Field>
                        <Form.Field width={8}>
                            <DatePicker
                                label={`${text("FilterBy")} ${text("ToDate")}`}
                                value={filterToDate}
                                onChange={setFilterToDate}
                                placeholder={text("SelectEndDate")}
                                clearable={false}
                            />
                        </Form.Field>
                        <Form.Field width={4} className='button-field'>
                            <Button 
                                {...buttonSize}
                                primary
                                onClick={clearFilters}
                                disabled={!filterFromDate && !filterToDate}
                            >
                                {text("Clear")}
                            </Button>
                        </Form.Field>
                    </Form.Group>
                    
                    {validationError && (
                        <div style={{ color: "red", marginBottom: "10px", fontSize: "0.9em" }}>
                            {validationError}
                        </div>
                    )}

                    <Form.Group>
                        <Form.Field width={8} ><DatePicker label={text("From")} onChange={setStartDate} clearable={false} value={startDate} /></Form.Field>
                        <Form.Field width={8} ><DatePicker label={text("To")} onChange={setEndDate} clearable={false} value={endDate} /></Form.Field>
                        <Form.Field width={4} className='button-field' ><Button primary {...buttonSize} onClick={addDateToList}>{text("AddDate")}</Button></Form.Field>
                    </Form.Group>

                    <div className="excluded-dates-list">
                        <Form.Group unstackable widths={'equal'} style={{ flexWrap: "wrap" }}>
                            {filteredExcludedDates.map((selectedDate, index) => {
                                const dateKey = formatRange(selectedDate.StartDate, selectedDate.EndDate);
                                const originalIndex = dateIndexMap.get(dateKey);
                                return (
                                    <div key={`${selectedDate.StartDate}-${selectedDate.EndDate}-${index}`} style={{ borderBottom: "1px solid rgba(34, 36, 38, 0.15)", marginBottom: "10px", display: "flex", alignItems: "center", justifyContent: "space-between", width: "95%" }}>
                                        <Label style={{ backgroundColor: "white", color: "black" }} as="a">
                                            From {moment(selectedDate.StartDate).format(dateFormat)} To {moment(selectedDate.EndDate).format(dateFormat)}
                                        </Label>
                                        <Icon name="delete" style={{ cursor: "pointer" }} onClick={(e) => onRemoveDateFromSelectedDates(originalIndex, e)} />
                                    </div>
                                );
                            })}
                        </Form.Group>
                    </div>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                {isBrowser && <Button disabled={isSaving} negative size={isMobileOnly ? "tiny" : "small"} onClick={closeAction}>{text("Close")}</Button>}
                {isBrowser && <Button disabled={currentPromotion && !currentPromotion.IsActive} loading={isSaving} positive size={isMobileOnly ? "tiny" : "small"} onClick={() => saveAction(true)}>{text("Saveandclose")}</Button>}
                <Button disabled={currentPromotion && !currentPromotion.IsActive} loading={isSaving} positive size={isMobileOnly ? "tiny" : "small"} onClick={() => saveAction(false)}>{text("Save")}</Button>
            </Modal.Actions>
        </Modal>
    )
}

ExcludedDatesPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onSaveComplete: PropTypes.func,
    currentPromotion: PropTypes.any,
};

export default ExcludedDatesPopup;
